import type { StoreWithSelectors } from '@client/types/zustand';
import { safeLocalStorage, safeSessionStorage } from '@client/utils/storage/safe-storage';
import superjson from 'superjson';
import { type StoreApi, useStore } from 'zustand';
import type { PersistStorage, StorageValue } from 'zustand/middleware';

export function createSelectorsForVanillaStore<S extends StoreApi<object>>(store: S) {
    const storeWithSelectors = store as StoreWithSelectors<typeof store>;

    storeWithSelectors.use = Object.keys(storeWithSelectors.getState()).reduce((prev, item) => {
        prev[item] = () => useStore(storeWithSelectors, (state) => state[item as keyof typeof state]);

        return prev;
    }, {} as any);

    return storeWithSelectors;
}

interface StoragePersisterOptions {
    writeDelayMs?: number;
    flushOnPageHide?: boolean;
}

function createStoragePersister<T>(
    storage: Storage,
    { writeDelayMs = 0, flushOnPageHide = false }: StoragePersisterOptions = {},
): PersistStorage<T> {
    const pendingWrites = new Map<string, StorageValue<T>>();
    let writeTimer: ReturnType<typeof setTimeout> | undefined;

    const write = (name: string, value: StorageValue<T>): void => {
        // A non-serialisable value (circular reference, unsupported type) must fail the
        // persist write only — never the state update that triggered it.
        let serialized: string;
        try {
            serialized = superjson.stringify(value);
        } catch (e) {
            if (import.meta.env.DEV) {
                console.warn(`[storagePersister] Skipping persist of "${name}": value is not serialisable`, e);
            }
            return;
        }
        storage.setItem(name, serialized);
    };

    const flushPendingWrites = (): void => {
        if (writeTimer !== undefined) {
            clearTimeout(writeTimer);
            writeTimer = undefined;
        }
        for (const [name, value] of pendingWrites) {
            write(name, value);
        }
        pendingWrites.clear();
    };

    if (flushOnPageHide && typeof window !== 'undefined') {
        window.addEventListener('pagehide', flushPendingWrites);
    }

    return {
        getItem: (name: string) => {
            const str = storage.getItem(name);
            if (!str) return null;
            try {
                return superjson.parse(str);
            } catch {
                storage.removeItem(name);
                return null;
            }
        },
        setItem: (name, value) => {
            if (writeDelayMs === 0) {
                write(name, value);
                return;
            }

            pendingWrites.set(name, value);
            if (writeTimer !== undefined) {
                clearTimeout(writeTimer);
            }
            writeTimer = setTimeout(flushPendingWrites, writeDelayMs);
        },
        removeItem: (name) => {
            pendingWrites.delete(name);
            if (pendingWrites.size === 0 && writeTimer !== undefined) {
                clearTimeout(writeTimer);
                writeTimer = undefined;
            }
            storage.removeItem(name);
        },
    };
}

/**
 * Creates a persister that uses localStorage and superjson to persist the store.
 * @returns
 */
export function createLocalStoragePersister<T>(): PersistStorage<T> {
    return createStoragePersister(safeLocalStorage);
}

/**
 * Creates a persister that uses sessionStorage and superjson to persist the store —
 * state survives reloads within the tab but clears when the tab is closed.
 */
export function createSessionStoragePersister<T>(): PersistStorage<T> {
    return createStoragePersister(safeSessionStorage, { writeDelayMs: 50, flushOnPageHide: true });
}
